import { batch, chain, chainIt, cycle, cycleN, dedup, dedupBy, enumerate, every, filter, find, flatMap, forEach, intersperse, ipairs, iter, length, map, pairs, peek, peekable, reduce, reduced, repeat, separate, skip, skipWhile, some, statefulMap, take, takeWhile, window, zip, } from './iter.js';
/**
 * A wrapper around sync {@link Iterable}s that also exposes the sync utility functions from this library as methods
 *
 * @category helpers
 */
export class IterWrapper {
    #impl;
    /**
     * Wrap an existing iterable
     *
     * @category wrapping
     * @see {@link IterWrapper.wrap} - the function to use instead of constructing manually
     */
    constructor(iterable) {
        this.#impl = iterable;
    }
    /**
     * Implement the {@link Iterable} protocol
     */
    [Symbol.iterator]() {
        return this.#impl[Symbol.iterator].call(this.#impl);
    }
    /**
     * Wrap an ordinary {@link Iterable}
     *
     * @category wrapping
     */
    static wrap(
    /**
     * The iterable to wrap
     */
    it) {
        if (it instanceof IterWrapper)
            return it;
        return new IterWrapper(it);
    }
    /**
     * Iterate over numbers between start (inclusive) and end (exclusive), with a step of step
     *
     * @category creators
     * @see {@link count}
     */
    static count(
    /**
     * The beginning of the range, inclusive
     *
     * @defaultValue 0
     */
    start, 
    /**
     * The step between two numbers
     *
     * @defaultValue 1
     */
    step, 
    /**
     * The end of the range, exclusive
     *
     * If step is positive, this should be greater than start, otherwise lesser than start
     *
     * @defaultValue +/- Infinity
     */
    end) {
        return IterWrapper.wrap(this.count(start, step, end));
    }
    /**
     * Repeat a given element a given amount of times or infinitely
     *
     * @category creators
     * @see {@link repeat}
     */
    static repeat(
    /**
     * The element to repeat
     */
    e, 
    /**
     * How many times to repeat the element
     *
     * @defaultValue Infinity
     */
    times) {
        return IterWrapper.wrap(repeat(e, times));
    }
    /**
     * Iterate over the entries of a plain object
     *
     * @category creators
     * @see {@link pairs}
     */
    static pairs(
    /**
     * The plain object to iterate over
     */
    obj) {
        return IterWrapper.wrap(pairs(obj));
    }
    /**
     * Iterate over the index and value of an array
     *
     * @category creators
     * @see {@link ipairs}
     */
    static ipairs(
    /**
     * The array to iterate over
     */
    arr) {
        return IterWrapper.wrap(ipairs(arr));
    }
    /**
     * Iterate over an iterator
     *
     * @category creators
     * @see {@link iter}
     */
    static iter(
    /**
     * The iterator to iterate over
     */
    it) {
        return IterWrapper.wrap(iter(it));
    }
    /**
     * Iterate over another iterable, separating every value with a given separator
     *
     * @category transformers
     * @see {@link separate}
     */
    separate(
    /**
     * The separator to injet between elements
     */
    separator) {
        return IterWrapper.wrap(separate(this.#impl, separator));
    }
    /**
     * Iterates over an iterable, returning tuples with one value and its position in the iteration
     *
     * Be careful, the order is [value, position], unlike the python `enumerate()` function which returns `(postition, value)`
     *
     * @category transformers
     * @see {@link enumerate}
     */
    enumerate() {
        return IterWrapper.wrap(enumerate(this.#impl));
    }
    filter(
    /**
     * The predicate to use
     */
    pred) {
        return IterWrapper.wrap(filter(this.#impl, pred));
    }
    takeWhile(
    /**
     * The predicate to use
     */
    pred) {
        return IterWrapper.wrap(takeWhile(this.#impl, pred));
    }
    /**
     * Returns a new iterable containing every value of the given iterable, starting when the predicate returns false for the first time
     *
     * @category transformers
     * @see {@link skipWhile}
     */
    skipWhile(
    /**
     * The predicate to use
     */
    pred) {
        return IterWrapper.wrap(skipWhile(this.#impl, pred));
    }
    /**
     * Returns a new iterable containing the mapped values of the given iterable, by the given transform
     *
     * @category transformers
     * @see {@link map}
     */
    map(
    /**
     * The transform to use
     */
    transform) {
        return IterWrapper.wrap(map(this.#impl, transform));
    }
    /**
     * Returns a new iterable containing the successive state values (except the initial one, unlike the python version) of the given reducer, applied to the given iterable
     *
     * @category transformers
     * @see {@link reduced}
     */
    reduced(
    /**
     * The reducer to use
     */
    reducer, 
    /**
     * Initial reducer state
     */
    initial) {
        return IterWrapper.wrap(reduced(this.#impl, reducer, initial));
    }
    /**
     * Returns a new iterable containing the mapped values (from a stateful map function) of the given iterable by the given map function
     *
     * @category transformers
     * @see {@link statefulMap}
     */
    statefulMap(
    /**
     * The stateful map function
     */
    reducer, 
    /**
     * Initial state
     */
    initial) {
        return IterWrapper.wrap(statefulMap(this.#impl, reducer, initial));
    }
    /**
     * Repeat the iterable forever
     *
     * @category transformers
     * @see {@link cycle}
     */
    cycle() {
        return IterWrapper.wrap(cycle(this.#impl));
    }
    /**
     * Repeat the iterable a given amount of times
     *
     * @category transformers
     * @see {@link cycleN}
     */
    cycleN(n) {
        if (n === 1)
            return this;
        else
            return IterWrapper.wrap(cycleN(this.#impl, n));
    }
    /**
     * Keep only the n (at most) first values
     *
     * @category transformers
     * @see {@link take}
     */
    take(n) {
        return IterWrapper.wrap(take(this.#impl, n));
    }
    /**
     * Skip the n first values
     *
     * @category transformers
     * @see {@link skip}
     */
    skip(n) {
        if (n <= 0)
            return this;
        return IterWrapper.wrap(skip(this.#impl, n));
    }
    /**
     * Return an iterable batching the items from the source iterable into blocks of n items
     *
     * The last block might be smaller if the total item count is not a multiple of n (unlike the rust implementation that exposes them differently, but like the python implementation)
     *
     * @category transformers
     * @see {@link batch}
     */
    batch(n) {
        return IterWrapper.wrap(batch(this.#impl, n));
    }
    /**
     * Returns an iterable containing windows around each element of this iterable, with the given amount of pre- and post- elements
     *
     * @category transformers
     * @see {@link window}
     */
    window(
    /**
     * How many elements to return in the pre- part
     */
    beforeCount, 
    /**
     * How many elements to return in the post- part
     */
    afterCount) {
        return IterWrapper.wrap(window(this.#impl, beforeCount, afterCount));
    }
    /**
     * Return an iterable containing the values of every iterable returned by calling the map on the provided iterable
     *
     * @category transformers
     * @see {@link flatMap}
     */
    flatMap(
    /**
     * The transform function, returning iterables
     */
    transform) {
        return IterWrapper.wrap(flatMap(this.#impl, transform));
    }
    /**
     * Deduplicate using a {@link Set}
     *
     * @category transformers
     * @see {@link dedup}
     */
    dedup() {
        return IterWrapper.wrap(dedup(this.#impl));
    }
    dedupBy(mapOrKey) {
        if (typeof mapOrKey === 'function') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return IterWrapper.wrap(dedupBy(this.#impl, mapOrKey));
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return IterWrapper.wrap(dedupBy(this.#impl, mapOrKey));
        }
    }
    /**
     * Iterate over every iterable in the list, retuning a value from the first, then the second, etc. then over again
     *
     * Finishes when any iterable finishes
     *
     * @category aggregators
     * @see {@link intersperse}
     */
    static intersperse(
    /**
     * The iterables to intersperse
     */
    ...iters) {
        return IterWrapper.wrap(intersperse(...iters));
    }
    /**
     * Zip together multiple iterables, returning tuples with one value from each iterable
     *
     * Finishes when any iterable finishes
     *
     * @category aggregators
     * @see {@link zip}
     */
    static zip(...iters) {
        return IterWrapper.wrap(zip(...iters));
    }
    /**
     * Returns a new iterable containing the values of each iterable, one iterable after the other
     *
     * Version with a vararg of iterables
     *
     * @category aggregators
     * @see {@link chain}
     */
    static chain(...iters) {
        if (iters.length === 1)
            return IterWrapper.wrap(iters[0]);
        else
            return IterWrapper.wrap(chain(...iters));
    }
    /**
     * Returns a new iterable containing the values of each iterable, one iterable after the other
     *
     * Version with an iterable of iterables
     *
     * @category aggregators
     * @see {@link chainIt}
     */
    static chainIt(iters) {
        return IterWrapper.wrap(chainIt(iters));
    }
    /**
     * Read the first n elements (at most) ofc the provided iterable, and return them along with an iterable that still contains them
     *
     * @category peeking
     * @see {@link peek}
     */
    peek(n) {
        if (!n)
            return [[], this];
        const [peeked, it] = peek(this.#impl, n);
        return [peeked, IterWrapper.wrap(it)];
    }
    /**
     * Returns an iterable that can be peeked (reading the top element without advancing), from a given iterable
     *
     * @category peeking
     * @see {@link peekable}
     */
    peekable() {
        const obj = peekable(this.#impl);
        return [obj, IterWrapper.wrap(obj)];
    }
    /**
     * Reduce down to a single value, with the given reducer
     *
     * @category collection
     * @see {@link reduce}
     */
    reduce(
    /**
     * The reducer to use
     */
    reducer, 
    /**
     * Initial state for the reducer
     */
    initial) {
        return reduce(this.#impl, reducer, initial);
    }
    find(
    /**
     * The predicate to find the value
     */
    predicate, 
    /**
     * The value to return if the value is not found
     */
    notFound) {
        return find(this.#impl, predicate, notFound);
    }
    /**
     * Iterates over the iterable and returns true (and stop iterating) if any value matches the predicate
     *
     * @category collection
     * @see {@link some}
     */
    some(
    /**
     * The predicate that should match
     */
    predicate) {
        return some(this.#impl, predicate);
    }
    /**
     * Iterates over the iterable and returns false (and stop iterating) if any value doesn't match the predicate
     *
     * @category collection
     * @see {@link every}
     */
    every(
    /**
     * The predicate that should match
     */
    predicate) {
        return every(this.#impl, predicate);
    }
    /**
     * Iterate over the entire iterable and return the number of items
     *
     * @category collection
     * @see {@link length}
     */
    length() {
        return length(this.#impl);
    }
    /**
     * Iterate over the entire iterable and run a function for every item
     *
     * @category collection
     * @see {@link forEach}
     */
    forEach(
    /**
     * The function to run on every item
     */
    fn) {
        forEach(this.#impl, fn);
    }
    /**
     * Iterate over the entire iterable and run a function for every item
     *
     * @category collection
     * @see {@link collect}
     */
    collect() {
        return [...this.#impl];
    }
}
//# sourceMappingURL=data:application/json;base64,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